/*
  VORYNEX Futuristic Showcase — glowing particle cloud, vanilla WebGL.

  Replaces an earlier scan/parallax-over-photo version after the user pointed
  at a reference (a gambling-operator landing page) with a warm, swirling
  glow-particle background reacting to the cursor. This is NOT a port of
  that specific 21st.dev component ("Quantum Nebula", id 9112) — its code
  was locked behind this session's daily retrieval limit (2/day, already
  spent on Futuristic Hero + Tilt). What's here is the same well-known,
  publicly-documented technique (turbulent noise-displaced point sprites,
  additive glow, no full bloom post-process pass) built from scratch,
  deliberately scaled down from the reference's 50,000 particles + real
  bloom pipeline: that combination is desktop-only-appropriate weight for a
  B2B marketing site, not for a mobile-heavy e-commerce storefront that
  already had a Core Web Vitals incident this session. PARTICLE_COUNT below
  is the one knob to raise later (with the real reference code, once the
  daily limit resets) if more density is wanted.

  Noise note: the displacement uses three offset samples of a classic
  Ashima Arts simplex noise (MIT-licensed, public-domain-equivalent, the
  standard GLSL noise everyone reaches for) rather than mathematically
  exact curl noise (which needs analytic partial derivatives of a
  potential field). Visually near-identical organic turbulence, far
  simpler code — called "turbulence" in the comments below rather than
  "curl noise" so this doesn't overclaim what it mathematically is.
*/
(() => {
  const sections = document.querySelectorAll('[data-vpx-fx]');
  if (!sections.length) return;

  const reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) return;

  const conn = navigator.connection || {};
  const wantsLightPage =
    conn.saveData === true || /(^|-)2g$/.test(conn.effectiveType || '');
  if (wantsLightPage) return;

  const hexToVec3 = (hex) => {
    const h = (hex || '#FF6B35').replace('#', '');
    const r = parseInt(h.substring(0, 2), 16) / 255;
    const g = parseInt(h.substring(2, 4), 16) / 255;
    const b = parseInt(h.substring(4, 6), 16) / 255;
    return [r, g, b];
  };

  // Mobile gets a lighter particle count — same visual language, cheaper.
  const isDesktop = window.matchMedia('(min-width: 750px)').matches;
  const PARTICLE_COUNT = isDesktop ? 4000 : 1800;

  // Ashima Arts webgl-noise (MIT license) — the standard classic-Perlin/
  // simplex GLSL noise used across countless WebGL projects.
  const noiseGLSL = `
    vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
    vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

    float snoise(vec3 v) {
      const vec2 C = vec2(1.0/6.0, 1.0/3.0);
      const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
      vec3 i  = floor(v + dot(v, C.yyy));
      vec3 x0 = v - i + dot(i, C.xxx);
      vec3 g = step(x0.yzx, x0.xyz);
      vec3 l = 1.0 - g;
      vec3 i1 = min(g.xyz, l.zxy);
      vec3 i2 = max(g.xyz, l.zxy);
      vec3 x1 = x0 - i1 + C.xxx;
      vec3 x2 = x0 - i2 + C.yyy;
      vec3 x3 = x0 - D.yyy;
      i = mod289(i);
      vec4 p = permute(permute(permute(
                i.z + vec4(0.0, i1.z, i2.z, 1.0))
              + i.y + vec4(0.0, i1.y, i2.y, 1.0))
              + i.x + vec4(0.0, i1.x, i2.x, 1.0));
      float n_ = 0.142857142857;
      vec3 ns = n_ * D.wyz - D.xzx;
      vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
      vec4 x_ = floor(j * ns.z);
      vec4 y_ = floor(j - 7.0 * x_);
      vec4 x = x_ *ns.x + ns.yyyy;
      vec4 y = y_ *ns.x + ns.yyyy;
      vec4 h = 1.0 - abs(x) - abs(y);
      vec4 b0 = vec4(x.xy, y.xy);
      vec4 b1 = vec4(x.zw, y.zw);
      vec4 s0 = floor(b0)*2.0 + 1.0;
      vec4 s1 = floor(b1)*2.0 + 1.0;
      vec4 sh = -step(h, vec4(0.0));
      vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
      vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
      vec3 p0 = vec3(a0.xy, h.x);
      vec3 p1 = vec3(a0.zw, h.y);
      vec3 p2 = vec3(a1.xy, h.z);
      vec3 p3 = vec3(a1.zw, h.w);
      vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
      p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
      vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
      m = m * m;
      return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
    }
  `;

  const vertexShader = `
    uniform float uTime;
    uniform vec2 uPointer;
    uniform float uPixelRatio;
    attribute float aSeed;
    varying float vSeed;
    varying float vAlpha;
    ${noiseGLSL}

    void main() {
      vSeed = aSeed;
      vec3 pos = position;

      // Turbulence: three offset noise samples give each axis an
      // independent, organically-correlated wobble over time.
      float t = uTime * 0.15;
      float nx = snoise(pos * 0.6 + vec3(t, 0.0, 0.0));
      float ny = snoise(pos * 0.6 + vec3(0.0, t, 5.2));
      float nz = snoise(pos * 0.6 + vec3(0.0, 5.2, t));
      pos += vec3(nx, ny, nz) * 0.6;

      // Cursor repulsion in screen-projected space, applied as a world-space
      // nudge — cheap approximation, no back-projection math needed for a
      // stylistic effect like this.
      vec2 toPointer = pos.xy - uPointer * 3.0;
      float d = length(toPointer);
      float repel = smoothstep(1.5, 0.0, d) * 0.8;
      pos.xy += normalize(toPointer + 0.0001) * repel;

      vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
      gl_Position = projectionMatrix * mvPosition;

      // Perspective attenuation constant tuned for this camera (z=5, fov 50)
      // against a ~540px stage — final on-screen sizes land around 3-14px,
      // individually visible dust specks rather than overlapping into one
      // saturated blob. The earlier value (300.0) was sized for a much
      // longer camera distance and blew every point out to 100-800px,
      // which additive-blended into a single white disc.
      float size = (2.0 + aSeed * 3.0) * uPixelRatio;
      gl_PointSize = size * (5.0 / -mvPosition.z);
      vAlpha = 0.22 + aSeed * 0.38;
    }
  `;

  const fragmentShader = `
    uniform vec3 uAccentA;
    uniform vec3 uAccentB;
    varying float vSeed;
    varying float vAlpha;

    void main() {
      // Soft circular sprite: alpha falls off from center, giving a glow
      // look with zero extra render passes (no bloom post-process needed).
      float d = length(gl_PointCoord - vec2(0.5));
      float alpha = smoothstep(0.5, 0.0, d) * vAlpha;
      vec3 color = mix(uAccentA, uAccentB, vSeed);
      gl_FragColor = vec4(color, alpha);
    }
  `;

  // The set-up below is heavy (a WebGL context, the extruded mark, its shaders): done in one go it held the page
  // 278 ms on a desktop, in the middle of a fast wheel scroll (measured 2026-10-06). It now runs in small steps, each
  // one only once the page has not scrolled for a moment, so scrolling never waits for it.
  let lastScroll = -1e9;
  addEventListener('scroll', () => { lastScroll = performance.now(); }, { passive: true });
  const quiet = () => new Promise((resolve) => {
    const check = () => {
      const since = performance.now() - lastScroll;
      if (since < 160) { setTimeout(check, 170 - since); return; }
      if (window.requestIdleCallback) requestIdleCallback(() => resolve(), { timeout: 800 });
      else setTimeout(resolve, 50);
    };
    check();
  });

  const initSection = async (section) => {
    // Per-element guard. This file is declared by the snippet, so a page
    // using the snippet AND the standalone section wrapper would execute it
    // twice — and the Theme Editor re-runs scripts on every section reload.
    // Without this, each pass would stack another render loop on the same
    // canvas: the exact "unattended rAF" failure this theme already paid for.
    if (section.dataset.vpxFxReady === 'true') return;
    section.dataset.vpxFxReady = 'true';

    const canvas = section.querySelector('[data-vpx-fx-canvas]');
    const poster = section.querySelector('.vpx-fx__poster');
    const stage = section.querySelector('.vpx-fx__stage');
    if (!canvas || !stage) return;
    if (!window.vpxThreeUrl) return;

    const THREE = await import(window.vpxThreeUrl).catch(() => null);
    if (!THREE) return;
    await quiet();

    const rect = stage.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(dpr);
    renderer.setSize(rect.width, rect.height, false);
    await quiet();

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, rect.width / rect.height, 0.1, 100);
    camera.position.z = 5;

    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    const seeds = new Float32Array(PARTICLE_COUNT);
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      // Distributed inside a flattened sphere so it reads as a cloud, not a cube.
      const r = Math.cbrt(Math.random()) * 2.2;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.7;
      positions[i * 3 + 2] = r * Math.cos(phi) * 0.6;
      seeds[i] = Math.random();
    }
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));

    const accentHex = section.dataset.vpxFxAccent || '#FF6B35';
    const accentA = hexToVec3(accentHex);
    const accentB = hexToVec3('#FF8A5C');

    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uPointer: { value: new THREE.Vector2(0, 0) },
        uPixelRatio: { value: dpr },
        uAccentA: { value: new THREE.Vector3(...accentA) },
        uAccentB: { value: new THREE.Vector3(...accentB) },
      },
      vertexShader,
      fragmentShader,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geometry, material);
    scene.add(points);

    /* ---------- The VORYNEX mark, as real extruded 3D geometry ----------

       Not an image of the logo on a plane: a plane has no edge, so the
       moment it turns past 90 degrees it vanishes into a hairline and the
       "3D" reads as a cheat. This builds actual solid geometry with depth
       and bevelled edges, so the rotation shows real thickness and the
       lights catch the chamfer.

       The outline is derived from the SAME two paths the site's footer mark
       uses (sections/vorynex-footer.liquid) rather than eyeballed, so this
       cannot drift out of sync with the brand mark:
         outer chevron: M4,4 L16,24 L28,4   stroke-width 3
         inner chevron: M9,4 L16,16 L23,4   stroke-width 2.5
       Those are strokes, and extrusion needs filled shapes, so each stroke
       is converted to its outline polygon below — offsetting both arms by
       half the stroke width and solving the miter at the point of the V.
    */
    const svgToLocal = (x, y) => [x - 16, 16 - y]; // centre, and flip SVG's y-down

    const chevronShape = (pts, strokeWidth) => {
      const h = strokeWidth / 2;
      const [a, b, c] = pts.map(([x, y]) => svgToLocal(x, y));

      // Unit normal of a segment, rotated +90deg (points into the V's mouth).
      const innerNormal = (p, q) => {
        const dx = q[0] - p[0];
        const dy = q[1] - p[1];
        const len = Math.hypot(dx, dy) || 1;
        return [-dy / len, dx / len];
      };
      const nLeft = innerNormal(a, b);
      const nRight = innerNormal(b, c);

      // Miter length at the vertex: h / sin(half-angle between the arms).
      const dirTo = (p, q) => {
        const dx = q[0] - p[0];
        const dy = q[1] - p[1];
        const len = Math.hypot(dx, dy) || 1;
        return [dx / len, dy / len];
      };
      const toA = dirTo(b, a);
      const toC = dirTo(b, c);
      const cosT = toA[0] * toC[0] + toA[1] * toC[1];
      const halfAngle = Math.acos(Math.max(-1, Math.min(1, cosT))) / 2;
      const miter = h / Math.max(Math.sin(halfAngle), 0.15); // clamp: never spike

      // Bisector, normalised — the miter runs along it, outward at the tip.
      const bisX = toA[0] + toC[0];
      const bisY = toA[1] + toC[1];
      const bisLen = Math.hypot(bisX, bisY) || 1;
      const bis = [bisX / bisLen, bisY / bisLen];

      const off = (p, n, s) => [p[0] + n[0] * s, p[1] + n[1] * s];

      const shape = new THREE.Shape();
      const outerA = off(a, nLeft, -h);
      shape.moveTo(outerA[0], outerA[1]);
      shape.lineTo(b[0] - bis[0] * miter, b[1] - bis[1] * miter); // outer miter
      const outerC = off(c, nRight, -h);
      shape.lineTo(outerC[0], outerC[1]);
      const innerC = off(c, nRight, h);
      shape.lineTo(innerC[0], innerC[1]);
      shape.lineTo(b[0] + bis[0] * miter, b[1] + bis[1] * miter); // inner miter
      const innerA = off(a, nLeft, h);
      shape.lineTo(innerA[0], innerA[1]);
      shape.closePath();
      return shape;
    };

    const extrudeSettings = {
      depth: 2.6,
      bevelEnabled: true,
      bevelThickness: 0.5,
      bevelSize: 0.4,
      bevelSegments: 3,
      curveSegments: 4,
    };

    await quiet();
    const markGroup = new THREE.Group();

    const outerGeo = new THREE.ExtrudeGeometry(
      chevronShape([[4, 4], [16, 24], [28, 4]], 3),
      extrudeSettings
    );
    outerGeo.center();
    const outerMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(accentHex),
      metalness: 0.85,
      roughness: 0.28,
    });
    markGroup.add(new THREE.Mesh(outerGeo, outerMat));

    const innerGeo = new THREE.ExtrudeGeometry(
      chevronShape([[9, 4], [16, 16], [23, 4]], 2.5),
      { ...extrudeSettings, depth: 3.4 }
    );
    innerGeo.center();
    // Sits slightly proud of the outer chevron so the two read as separate
    // machined parts rather than one flat sticker.
    innerGeo.translate(0, -1.1, 0.6);
    const innerMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color('#FF8A5C'),
      metalness: 0.9,
      roughness: 0.18,
      emissive: new THREE.Color(accentHex),
      emissiveIntensity: 0.18,
    });
    markGroup.add(new THREE.Mesh(innerGeo, innerMat));

    markGroup.scale.setScalar(0.1);
    markGroup.position.z = 1.4; // in front of most of the cloud, but not all
    scene.add(markGroup);

    // Lighting: one warm key from upper-left, one cool rim from behind-right.
    // The rim is what makes the bevelled edges legible against a dark field —
    // without it the mark reads as a flat silhouette while it turns.
    scene.add(new THREE.AmbientLight(0xffffff, 0.35));
    const keyLight = new THREE.DirectionalLight(0xffd9c0, 2.4);
    keyLight.position.set(-3, 4, 5);
    scene.add(keyLight);
    const rimLight = new THREE.DirectionalLight(new THREE.Color(accentHex), 3.2);
    rimLight.position.set(4, -2, -3);
    scene.add(rimLight);

    // Shaders compiled before the first frame, so the first render doesn't compile them on the spot.
    await quiet();
    if (renderer.compileAsync) await renderer.compileAsync(scene, camera).catch(() => {});
    await quiet();

    /* ---------- Click to spin ----------
       A full 360 on the Y axis, eased, and interruptible-by-completion
       rather than queued: clicking mid-spin restarts from the current
       angle instead of stacking rotations, so hammering the mark can
       never leave it spinning for ten seconds after the last click.
    */
    const SPIN_DURATION = 1.15;
    let spinStart = -1;
    let spinFrom = 0;

    const triggerSpin = () => {
      spinFrom = markGroup.rotation.y;
      spinStart = clock.getElapsedTime();
    };

    stage.addEventListener('click', triggerSpin);
    stage.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        triggerSpin();
      }
    });

    let pointerTarget = { x: 0, y: 0 };
    let pointerCurrent = { x: 0, y: 0 };
    stage.addEventListener('pointermove', (e) => {
      const r = stage.getBoundingClientRect();
      pointerTarget.x = ((e.clientX - r.left) / r.width - 0.5) * 2;
      pointerTarget.y = -((e.clientY - r.top) / r.height - 0.5) * 2;
    });
    stage.addEventListener('pointerleave', () => {
      pointerTarget.x = 0;
      pointerTarget.y = 0;
    });

    let running = true;
    let rafId = null;
    const clock = new THREE.Clock();

    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const tick = () => {
      if (!running) return;
      const elapsed = clock.getElapsedTime();

      pointerCurrent.x += (pointerTarget.x - pointerCurrent.x) * 0.06;
      pointerCurrent.y += (pointerTarget.y - pointerCurrent.y) * 0.06;
      material.uniforms.uPointer.value.set(pointerCurrent.x, pointerCurrent.y);
      material.uniforms.uTime.value = elapsed;
      points.rotation.y = elapsed * 0.03;

      if (spinStart >= 0) {
        const t = (elapsed - spinStart) / SPIN_DURATION;
        if (t >= 1) {
          markGroup.rotation.y = spinFrom;
          spinStart = -1;
        } else {
          markGroup.rotation.y = spinFrom + easeInOutCubic(t) * Math.PI * 2;
        }
      } else {
        // Idle: the mark leans toward the cursor. Y tracks horizontal
        // pointer, X vertical — the same read as physically tilting an
        // object you're holding, not an arbitrary wobble.
        markGroup.rotation.y += (pointerCurrent.x * 0.45 - markGroup.rotation.y) * 0.06;
      }
      markGroup.rotation.x += (pointerCurrent.y * -0.3 - markGroup.rotation.x) * 0.06;

      renderer.render(scene, camera);
      rafId = requestAnimationFrame(tick);
    };

    canvas.classList.add('is-ready');
    tick();

    // Same off-screen pause discipline as the rest of this theme's motion
    // code — never an unattended rAF loop, the exact failure mode that
    // burned 6,673ms of main thread before this site's Core Web Vitals fix.
    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          running = entry.isIntersecting;
          if (running && !rafId) tick();
          if (!running && rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
        });
      },
      { threshold: 0.01 }
    );
    visibilityObserver.observe(section);

    document.addEventListener('visibilitychange', () => {
      running = document.visibilityState === 'visible' && running;
      if (!running && rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      } else if (running && !rafId) {
        tick();
      }
    });
  };

  const loadObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          initSection(entry.target);
          loadObserver.unobserve(entry.target);
        }
      });
    },
    // Started further ahead (was 400px), so the steps above are done before the visitor gets there.
    { rootMargin: '150% 0px' }
  );

  sections.forEach((section) => loadObserver.observe(section));
})();
